/**
 * API Configuration
 *
 * Centralized configuration for all API endpoints used in the application.
 * Uses environment variables with proper validation and fallbacks.
 */

/**
 * Stable per-tab correlation ID — generated once at module load.
 * Included on every outbound request so a single user session can be traced
 * across all 5 backend services in CloudWatch Logs Insights.
 */
export const sessionCorrelationId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Build standard request headers including the correlation ID.
 * Usage: fetch(url, { headers: getBaseHeaders(token) })
 *
 * @param {string} [authToken] - Bearer token from Cognito (omit for unauthenticated requests)
 * @returns {Record<string, string>}
 */
export const getBaseHeaders = (authToken) => ({
  'Content-Type': 'application/json',
  ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  'X-Correlation-Id': sessionCorrelationId,
  'X-Client': 'infhire-web',
});

// Environment variables validation
const validateEnvironmentVariables = () => {
  const requiredVars = [
    'REACT_APP_CORE_API_URL',
    'REACT_APP_INFLUENCERS_API_URL',
    'REACT_APP_AUTOMATION_API_URL',
    'REACT_APP_COMMUNICATION_API_URL',
    'REACT_APP_CAMPAIGNS_API_URL',
  ];

  const missingVars = requiredVars.filter(varName => !process.env[varName]);

  if (missingVars.length > 0) {
    console.warn('Missing environment variables:', missingVars);
    console.warn('Add them to your .env.development file. See frontend/.env.example for the full list.');
  }

  return missingVars.length === 0;
};

// Validate environment on startup
const hasValidEnv = validateEnvironmentVariables();

const apiConfig = {
  // Core API for authentication and user management
  core: process.env.REACT_APP_CORE_API_URL,

  // Influencers API for search and discovery
  influencers: process.env.REACT_APP_INFLUENCERS_API_URL,

  // Automation API for YouTube collection and automation
  automation: process.env.REACT_APP_AUTOMATION_API_URL,

  // Communication API for messaging and user search
  communication: process.env.REACT_APP_COMMUNICATION_API_URL,

  // Campaigns API for campaign management, applications, and posts
  campaigns: process.env.REACT_APP_CAMPAIGNS_API_URL,

  // Environment information
  environment: process.env.REACT_APP_ENVIRONMENT || 'development',
  version: process.env.REACT_APP_VERSION || '0.1.0',
  debugMode: process.env.REACT_APP_DEBUG_MODE === 'true',
  apiTimeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
  
  // Validation status
  isEnvironmentValid: hasValidEnv,
  
  // Helper method to get the base URL for a specific API
  getBaseUrl: (api) => {
    if (!apiConfig[api]) {
      console.warn(`API "${api}" not found in configuration. Using core API as fallback.`);
      return apiConfig.core;
    }
    return apiConfig[api];
  },
  
  // Helper method to build a full endpoint URL
  getEndpoint: (api, path) => {
    const baseUrl = apiConfig.getBaseUrl(api);
    // Ensure path starts with / and baseUrl doesn't end with /
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    const formattedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    
    return `${formattedBaseUrl}${formattedPath}`;
  },
  
  // Helper method to validate API URL format
  validateApiUrl: (url) => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:';
    } catch (error) {
      return false;
    }
  },
  
  // Get configuration summary for debugging
  getConfigSummary: () => {
    return {
      environment: apiConfig.environment,
      version: apiConfig.version,
      debugMode: apiConfig.debugMode,
      apiTimeout: apiConfig.apiTimeout,
      isEnvironmentValid: apiConfig.isEnvironmentValid,
      endpoints: {
        core: apiConfig.core,
        influencers: apiConfig.influencers,
        automation: apiConfig.automation,
        communication: apiConfig.communication,
        campaigns: apiConfig.campaigns
      },
      missingEnvVars: [
        'REACT_APP_CORE_API_URL',
        'REACT_APP_INFLUENCERS_API_URL',
        'REACT_APP_AUTOMATION_API_URL',
        'REACT_APP_COMMUNICATION_API_URL',
        'REACT_APP_CAMPAIGNS_API_URL',
      ].filter(v => !process.env[v])
    };
  }
};

// Log configuration in development mode
if (apiConfig.debugMode) {
  console.log('API Configuration:', apiConfig.getConfigSummary());
}

export default apiConfig;
